import { ProfileSizes, User } from "@ecologi/shared";
import styled from "@emotion/styled";
import classnames from "classnames";
import React, { useMemo } from "react";
import { DateTime } from "luxon";
import { InlineBanner } from "../InlineBanner";
import { Link } from "../Link";
import { useRouter } from "next/router";
import { ProfileAvatar } from "../ProfileAvatar";
import { Typography } from "../Typography";
import styles from "./ProfileHeader.module.scss";
import { SubforestInfo } from "../SubforestInfo";

export type ProfileHeaderProps = {
  children?: React.ReactNode;
  isOwnProfile: boolean;
  profileTitle: { title: string; intro?: string };
  resumeSubscription: () => Promise<void>;
  size: ProfileSizes;
  user: User;
};

const ProfileTitle = styled(Typography)`
  margin-bottom: 14px;
  word-wrap: break-word;
`;

const LinkWrapper = styled.div<{ marginBottom: string }>`
  margin-bottom: ${(props) => props.marginBottom};
  overflow: auto;
`;

export const ProfileHeader = ({
  children,
  isOwnProfile,
  profileTitle,
  resumeSubscription,
  size,
  user,
}: ProfileHeaderProps) => {
  const router = useRouter();

  const createdAtYear = DateTime.fromJSDate(user.createdAt).year;
  const isFoundingMember =
    user.customerNumber === 0
      ? false
      : user.isBusinessPlan
      ? user.customerNumber < 101
      : user.customerNumber < 1001;
  const memberNumberCopy = isFoundingMember
    ? `Founding member since ${createdAtYear}`
    : `Climate impact since ${createdAtYear}`;

  // Prevents urls from being appended onto the current path, by prepending https
  const sanitizedUrl = useMemo(() => {
    const url = user.link;
    if (!url) return null;

    if (url.startsWith("https") || url.startsWith("http")) return url;

    return `https://${url}`;
  }, [user.link]);

  return (
    <section className={styles.profileText} id="tour-profile">
      <div className={styles.profileImage}>
        <ProfileAvatar
          size={size}
          alt={user.fullName()}
          src={user.getProfileImage()}
          data-testid={
            user.getProfileImage().includes("/images/app/avatars/")
              ? undefined
              : "user-uploaded-profile-picture"
          }
        />
      </div>
      {profileTitle && profileTitle.intro && (
        <Typography use="componentTitle" as="h2">
          {profileTitle.intro}
        </Typography>
      )}
      <ProfileTitle use="featureTitle" as="h1">
        {profileTitle?.title || user.fullName()}
      </ProfileTitle>
      {user.city && user.country && (
        <Typography use="subtitle2" as="h3" style={{ marginBottom: "12px" }}>
          {[user.city, user.country].filter((x) => !!x).join(", ")}
        </Typography>
      )}
      {user.blurb && (
        <p
          className={classnames(styles.blurb, {
            [styles.blurbLarge]: size === ProfileSizes.LARGE,
            [styles.blurbXLarge]: size === ProfileSizes.XLARGE,
          })}
        >
          {user.blurb}
        </p>
      )}
      {sanitizedUrl && (
        <LinkWrapper marginBottom="10px">
          <a
            className={styles.link}
            title={`View the ${user.fullName()} website (new tab)`}
            href={sanitizedUrl}
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            <Typography use="body2" as="p">
              {user.linkClean}
            </Typography>
          </a>
        </LinkWrapper>
      )}
      {user.isYearOfFirstImpactEnabled && (
        <div className={styles.customerNumber}>{memberNumberCopy}</div>
      )}
      {user.isChildProfile && user.parentProfile && (
        <SubforestInfo
          parentUsername={user.parentProfile.username}
          parentForestName={user.parentProfile.forestName || ""}
          parentImageSrc={user.getParentProfileImage()}
        />
      )}
      {children}
      <div className={styles.bannerWrapper} data-testid="invalid-input-banner">
        {isOwnProfile && user.isPaused && (
          <InlineBanner
            title="Your account is currently paused"
            action={{
              content: "Resume subscription",
              onAction: resumeSubscription,
            }}
            status="info"
          >
            <p>
              {`Continue your contribution to climate crisis solutions by resuming
              your subscription using the button below. Please note that changes
              could have been made to our plans while you were away. `}
              <Link
                href={user.isBusinessType ? "/business/checkout " : "/plan"}
                title="Check our plans here"
              >
                Check our plans here
              </Link>
              {`.`}
            </p>
          </InlineBanner>
        )}
        {isOwnProfile &&
          user.subscription?.isFailingPayment &&
          !user.isPayByInvoiceCustomer && (
            <InlineBanner
              title="There is a problem with your payment details"
              action={{
                content: "Update card",
                onAction: () => {
                  router.push("/account/billing");
                },
              }}
              status="info"
            >
              <p>
                We have been unable to charge your subscription for this month,
                please update your billing details to contribute to climate
                crisis solutions.
              </p>
            </InlineBanner>
          )}
      </div>
    </section>
  );
};
